import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Save, Upload, Loader2, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ProjectAvatar, HealthDot } from '@/components/ProjectAvatar';
import { BrandIdentityCard } from '@/components/BrandIdentityCard';
import { useProjects, useProjectsHealth } from '@/components/ProjectSwitcher';
import { apiFetch, getProject, setProject, uploadProjectLogo } from '@/lib/api';
import { healthOf, isOfficial, type Project } from '@/lib/projects';
import { NewProjectDialog } from '@/components/NewProjectDialog';
import { useT } from '@/lib/i18n';

export function Projects() {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: projects = [] } = useProjects();
  const { data: health = {} } = useProjectsHealth();
  const activeId = getProject();
  const [selId, setSelId] = useState<string | null>(null);

  // Seleciona o projeto ativo (ou o primeiro) ao carregar a lista.
  useEffect(() => {
    if (!selId && projects.length) setSelId(activeId || projects[0].id);
  }, [selId, projects, activeId]);

  const selected = projects.find((p) => p.id === selId) ?? null;

  const [creating, setCreating] = useState(false);

  const onCreated = (p: Project) => {
    qc.invalidateQueries({ queryKey: ['projects'] });
    setSelId(p.id);
    // Projeto oficial nasce sem número e com um caminho claro pela frente: em
    // vez de largar o cliente numa tela vazia, ele já entra no passo a passo.
    if (isOfficial(p)) {
      setProject(p.id);
      navigate('/numbers');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{t('project.pageSubtitle')}</p>
        <Button className="w-full shrink-0 sm:w-auto" onClick={() => setCreating(true)}>
          <Plus className="size-4" /> {t('project.new')}
        </Button>
      </div>

      <NewProjectDialog open={creating} onOpenChange={setCreating} onCreated={onCreated} />

      <div className="grid gap-6 md:grid-cols-[18rem_1fr]">
        {/* Lista de projetos */}
        <Card className="h-fit">
          <CardContent className="flex flex-col gap-1 p-2">
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelId(p.id)}
                className={
                  'flex items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors ' +
                  (p.id === selId ? 'bg-accent' : 'hover:bg-accent/50')
                }
              >
                <ProjectAvatar project={p} size={32} />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 truncate font-medium">
                    {p.name}
                    {p.is_default && <Star className="size-3 fill-amber-400 text-amber-400" />}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {healthSummary(t, health[p.id])}
                  </p>
                </div>
                <HealthDot health={healthOf(health[p.id])} />
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Editor do projeto selecionado */}
        {selected ? (
          <ProjectEditor
            key={selected.id}
            project={selected}
            isActive={selected.id === activeId}
            isLast={projects.length <= 1}
            onChanged={() => qc.invalidateQueries({ queryKey: ['projects'] })}
            onDeleted={() => {
              setSelId(null);
              qc.invalidateQueries({ queryKey: ['projects'] });
            }}
          />
        ) : (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              {t('project.selectHint')}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function healthSummary(
  t: ReturnType<typeof useT>,
  h?: { total: number; statuses: Record<string, number> },
): string {
  if (!h || h.total === 0) return t('project.noNumbers');
  // warming = online (aquecendo), conta como conectado no resumo.
  const connected = (h.statuses?.connected ?? 0) + (h.statuses?.warming ?? 0);
  return t('project.numbersSummary', { connected, total: h.total });
}

function ProjectEditor({
  project,
  isActive,
  isLast,
  onChanged,
  onDeleted,
}: {
  project: Project;
  isActive: boolean;
  isLast: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(project.name);
  const [color, setColor] = useState(project.color ?? '');
  const [logoUrl, setLogoUrl] = useState(project.logo_url ?? '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const save = async () => {
    if (!name.trim()) {
      toast.error(t('project.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      const r = await apiFetch('PATCH', `/projects/${project.id}`, {
        name: name.trim(),
        logo_url: logoUrl,
        color,
      });
      if (r.ok) {
        toast.success(t('project.saved'));
        onChanged();
      } else toast.error(t('project.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const onLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadProjectLogo(project.id, file);
      setLogoUrl(url);
      toast.success(t('project.logoUploaded'));
      onChanged();
    } catch (err) {
      toast.error(
        (err as Error).message === 'uploads_disabled'
          ? t('brand.uploadsDisabled')
          : t('project.logoFailed'),
      );
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const del = async () => {
    if (!window.confirm(t('project.confirmDelete', { name: project.name }))) return;
    setDeleting(true);
    try {
      const r = await apiFetch('DELETE', `/projects/${project.id}`);
      if (r.ok) {
        toast.success(t('project.deleted'));
        if (isActive) setProject(''); // volta ao padrão no próximo carregamento
        onDeleted();
      } else if ((r.data as { code?: string })?.code === 'last_project') {
        toast.error(t('project.lastProject'));
      } else toast.error(t('project.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Detalhes + identidade visual */}
      <Card>
        <CardContent className="flex flex-col gap-5 p-5">
          <div>
            <h2 className="text-base font-semibold">{t('project.detailsTitle')}</h2>
            <p className="text-sm text-muted-foreground">{t('project.detailsSubtitle')}</p>
          </div>

          <div className="flex items-center gap-4">
            <ProjectAvatar project={{ name, logo_url: logoUrl, color }} size={56} />
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={onLogo}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Upload className="size-3.5" />
                )}{' '}
                {t('project.logoUpload')}
              </Button>
              <p className="text-xs text-muted-foreground">{t('project.logoHint')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>{t('project.name')}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('project.color')}</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label={t('project.color')}
                  value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#6366f1'}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-10 shrink-0 cursor-pointer rounded-md border bg-transparent p-1"
                />
                <Input
                  value={color}
                  onChange={(e) => {
                    let v = e.target.value.trim();
                    if (v && !v.startsWith('#')) v = '#' + v;
                    setColor(v);
                  }}
                  placeholder="#6366f1"
                  maxLength={7}
                  className="font-mono"
                />
                {color && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setColor('')}>
                    {t('common.clear')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end border-t pt-4">
            <Button disabled={saving} onClick={save}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}{' '}
              {t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Identidade dos NÚMEROS do projeto */}
      <BrandIdentityCard projectId={project.id} canApply={isActive} />

      {/* Zona de risco */}
      {!project.is_default && (
        <Card className="border-destructive/30">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
            <div>
              <h2 className="text-base font-semibold text-destructive">
                {t('project.dangerTitle')}
              </h2>
              <p className="text-sm text-muted-foreground">{t('project.dangerSubtitle')}</p>
            </div>
            <Button variant="destructive" disabled={deleting || isLast} onClick={del}>
              {deleting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Trash2 className="size-4" />
              )}{' '}
              {t('project.delete')}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
